import React, { useCallback, useEffect, useState } from 'react';
import { EuiForm, EuiSpacer } from '@elastic/eui';
import TextField from '../../../components/Form/TextField';
import NumberField from '../../../components/Form/NumberField';
import ButtonGroup from '../../../components/Form/ButtonGroup';
import FormGroupDescription from '../../../components/formGroupDescription';
import { useDispatch, useSelector } from 'react-redux';
import { restaurantSettingsAPI } from '../../../api/restaurantSettings/restaurantSettingsAPI';
import useFormActionsHandler from '../../../hooks/useFormActionsHandler';
import SplashScreen from '../Components/SplashScreen/SplashScreen';

const AppSettings = (props) => {
    const { restaurantResponse, fetchRestaurant } = props;
    const [defaultValues, setDefaultValues] = useState({});

    const dispatch = useDispatch();
    const restaurantDescriptions = useSelector((state) => state.auth.restaurantDescriptions);
    const availableLanguages = useSelector((state) => state.language.availableLanguages);

    const updateFormValues = useCallback((data) => {
        let defaultValue = {};

        defaultValue.name = data.name;
        defaultValue.app_name = data.settings && data.settings.app_name;
        defaultValue.apply_mask = data.settings.apply_mask;
        defaultValue.logo_height = data.settings ? data.settings.logo_height : null;
        defaultValue.is_customer_email_required = data.settings.is_customer_email_required;
        defaultValue.repeat_usage_time = data.settings.repeat_usage_time;
        defaultValue.app_rating_enabled = data?.settings?.app_rating_enabled ?? 0;
        defaultValue.show_save_badge = data?.settings?.show_save_badge ?? 0;
        // Handle splash screen data - check both locations (data.splash_screen and data.settings.splash_screen)
        const splashData = data.splash_screen || (data.settings && data.settings.splash_screen);

        if (splashData) {
            let splashType = 'static';

            // Check the type field directly first (new API format)
            if (splashData.type) {
                splashType = splashData.type;
            }
            // Fallback to file_type mapping (old API format)
            else if (splashData.file_type) {
                if (splashData.file_type === 'video') {
                    splashType = 'video';
                } else if (splashData.file_type === 'image') {
                    splashType = 'image';
                } else if (splashData.file_type === 'json') {
                    splashType = 'animated';
                }
            }

            defaultValue.splash_screen = {
                type: splashType,
                url: splashData.url, // Direct url property
                file_path: splashData.url || splashData.file_path, // Use url if available, fallback to file_path
                file_type: splashData.type || splashData.file_type,
                fileName:
                    splashData.url || splashData.file_path
                        ? (splashData.url || splashData.file_path).split('/').pop()
                        : null,
                mime: splashData.mime,
                size_in_mb: splashData.size_in_mb,
            };
        } else {
            defaultValue.splash_screen = { type: 'static' };
        }

        defaultValue.languages = data.languages.map((item, index) => ({
            label: item.language_label,
            language: item.language,
            language_label: item.language_label,
        }));

        let obj = {};

        watch('languages')?.forEach((language) => {
            if (language.language_label != 'English') {
                obj[language.language] = '';
            }
        });

        let translationObject = {
            restaurant_name: obj,
        };

        for (const language_label in data.translations.restaurant_name) {
            translationObject.restaurant_name[language_label] = data.translations.restaurant_name[language_label];
        }

        defaultValue.translations = translationObject;

        return defaultValue;
    }, []);

    useEffect(() => {
        setDefaultValues(updateFormValues(restaurantResponse.restaurant));
    }, [restaurantResponse.restaurant, updateFormValues]);

    const onFormSaveApi = useCallback(
        async (data) => {
            if (data.translations) {
                availableLanguages.map((language) => {
                    if (language.language_label === 'English') {
                        data.translations.restaurant_name[language.language] = data.name;
                    }
                });
            } else {
                data.translations = { restaurant_name: { en: data.name } };
            }

            // Format splash screen data for API
            if (data.splash_screen) {
                const splashType = data.splash_screen.type;

                // If 'static' is selected, pass null to the API
                if (splashType === 'static') {
                    data.splash_type = null;
                } else {
                    data.splash_type = splashType;
                }

                // Remove the splash_screen object as the API expects splash_type
                delete data.splash_screen;
            }

            const response = await dispatch(restaurantSettingsAPI('app_settings', data));
            if (response.success) {
                fetchRestaurant();
            } else {
                alert(response.message);
            }

            if (response.success) setDefaultValues(updateFormValues(response.restaurant));
            return response;
        },
        [dispatch, updateFormValues, availableLanguages, fetchRestaurant]
    );

    const { errors, control, watch, setValue } = useFormActionsHandler({
        onFormSaveApi: onFormSaveApi,
        defaultValues: defaultValues,
    });

    return (
        <>
            <EuiForm component='form'>
                <FormGroupDescription
                    title={restaurantDescriptions?.name_of_restaurant?.display_name}
                    description={restaurantDescriptions?.name_of_restaurant?.description}
                >
                    <TextField
                        errors={errors}
                        name='name'
                        placeholder='Restaurant name: English'
                        rules={{ required: 'Please enter name' }}
                        control={control}
                        watch={watch}
                    />
                    {watch('languages') &&
                        watch('languages').length > 0 &&
                        watch('languages').map((item, index) => (
                            <>
                                {item.language_label != 'English' ? (
                                    <TextField
                                        errors={errors}
                                        isInvalid={
                                            errors.translations &&
                                            errors.translations.restaurant_name &&
                                            errors.translations.restaurant_name.hasOwnProperty(item.language)
                                        }
                                        error={
                                            errors.translations &&
                                            errors.translations.restaurant_name &&
                                            errors.translations.restaurant_name.hasOwnProperty(item.language) &&
                                            errors.translations.restaurant_name[item.language]
                                                ? errors.translations.restaurant_name[item.language].message
                                                : ''
                                        }
                                        // label={index === 0 ? <strong>Restaurant name</strong> : ''}
                                        name={`translations.restaurant_name.${item.language}`}
                                        placeholder={`Restaurant name: ${item.language_label} `}
                                        rules={{ required: 'Please write restaurant name ' }}
                                        control={control}
                                        watch={watch}
                                        rows={4}
                                    />
                                ) : (
                                    ''
                                )}
                            </>
                        ))}
                </FormGroupDescription>
                <FormGroupDescription
                    title={restaurantDescriptions?.application_display_name?.display_name}
                    description={restaurantDescriptions?.application_display_name?.description}
                >
                    <TextField
                        errors={errors}
                        // label='Application display name'
                        name='app_name'
                        placeholder='Enter the display name of the application'
                        rules={{ required: 'Please Enter your application name' }}
                        control={control}
                        watch={watch}
                    />
                </FormGroupDescription>
                <FormGroupDescription
                    title={restaurantDescriptions?.customer_email_compulsory?.display_name}
                    description={restaurantDescriptions?.customer_email_compulsory?.description}
                >
                    <ButtonGroup
                        idSelected={watch('is_customer_email_required') ? '1' : '0'}
                        color='primary'
                        control={control}
                        setValue={setValue}
                        name='is_customer_email_required'
                        watch={watch}
                    />
                </FormGroupDescription>
                <FormGroupDescription
                    title={restaurantDescriptions?.apply_mask_on_category_images?.display_name}
                    description={restaurantDescriptions?.apply_mask_on_category_images?.description}
                >
                    <ButtonGroup
                        idSelected={watch('apply_mask') ? '1' : '0'}
                        color='primary'
                        control={control}
                        setValue={setValue}
                        name='apply_mask'
                        watch={watch}
                    />
                </FormGroupDescription>
                <FormGroupDescription
                    title={restaurantDescriptions?.logo_height?.display_name}
                    description={restaurantDescriptions?.logo_height?.description}
                >
                    <NumberField
                        errors={errors}
                        name='logo_height'
                        placeholder='Please Enter logo height'
                        control={control}
                    />
                </FormGroupDescription>
                <SplashScreen
                    watch={watch}
                    control={control}
                    setValue={setValue}
                    defaultValue={restaurantResponse.restaurant}
                />
                <FormGroupDescription
                    title={restaurantDescriptions?.offer_usage_time?.display_name}
                    description={restaurantDescriptions?.offer_usage_time?.description}
                >
                    <NumberField
                        errors={errors}
                        name='repeat_usage_time'
                        placeholder='Please Enter usage time'
                        control={control}
                        prepend='Mins'
                    />
                </FormGroupDescription>
                <FormGroupDescription
                    title={restaurantDescriptions?.app_rating_popup?.display_name || 'Rating Popup'}
                    description={
                        restaurantDescriptions?.app_rating_popup?.description ||
                        'When enabled, customers will see a popup to add an app review after order completion.'
                    }
                >
                    <ButtonGroup
                        idSelected={watch('app_rating_enabled') ? '1' : '0'}
                        color='primary'
                        control={control}
                        setValue={setValue}
                        name='app_rating_enabled'
                        watch={watch}
                        legend='Enable rating popup'
                    />
                </FormGroupDescription>
                <FormGroupDescription
                    title={restaurantDescriptions?.show_save_badge?.display_name || 'Show Save Badge'}
                    description={
                        restaurantDescriptions?.show_save_badge?.description ||
                        'When enabled, customers will see a save badge on the app.'
                    }
                >
                    <ButtonGroup
                        idSelected={watch('show_save_badge') ? '1' : '0'}
                        color='primary'
                        control={control}
                        setValue={setValue}
                        name='show_save_badge'
                        watch={watch}
                        legend='Enable show save badge'
                    />
                </FormGroupDescription>
                <EuiSpacer size='l' />
            </EuiForm>
        </>
    );
};

export default AppSettings;
